/**
 * Layout-independent letter shortcuts.
 *
 * The annotators are driven by one-key shortcuts (Q/W to judge, Q/W/E/R to toggle the
 * state flags, F/E for full/empty). Read off `event.key` those only fire in a latin
 * layout: a labeller working in Russian presses the very same physical key and the
 * browser reports «й», so the shortcut silently does nothing and the crop is skipped.
 * Labellers hit this constantly — they type Russian all day and the annotator is the one
 * place that demands they switch.
 *
 * `event.code` names the physical KEY rather than the character it produced, so «й» and
 * «q» are both `KeyQ` and one comparison covers every layout. It is unaffected by the
 * OS-level layout switch, which is exactly the thing that breaks `event.key`.
 */

/** Minimal shape both DOM and React keyboard events satisfy. */
interface KeyLike {
  key: string;
  code?: string;
}

/**
 * The latin letter a keypress corresponds to, whatever the active layout — or `''` if
 * the key is not a letter (digits, arrows, punctuation: compare those on `event.key`,
 * which already behaves the same in every layout we support).
 */
export function layoutSafeLetter(e: KeyLike): string {
  // 'KeyQ' → 'q'. Deliberately NOT `startsWith('Key')` alone: `code` also carries
  // multi-character names and we only want the single-letter block.
  const code = e.code;
  if (code && code.length === 4 && code.startsWith('Key')) {
    return code[3].toLowerCase();
  }
  // No `code` at all (synthetic events in tests, exotic IMEs) — fall back to the
  // character. That is the pre-existing behaviour, so nothing gets worse.
  const k = (e.key || '').toLowerCase();
  return k.length === 1 && k >= 'a' && k <= 'z' ? k : '';
}
